export const select = () => {
  const customSelectInit = (customSelect: HTMLElement) => {
    /* Key Codes */
    const ENTER_KEY_CODE = "Enter";
    const ESCAPE_KEY_CODE = "Escape";
    const UP_KEY_CODE = "ArrowUp";
    const DOWN_KEY_CODE = "ArrowDown";
    const CHEVRON_UP = "up";
    const CHEVRON_DOWN = "down";

    /* Is there an attrib defining the ID of a select element to update when this one is changed? */
    const targetFormSelectId = customSelect.getAttribute(
      "js-custom-select-form"
    );
    /* If so, get a reference to it */
    const targetFormSelect = targetFormSelectId
      ? document.querySelector<HTMLSelectElement>(
          `[name="${targetFormSelectId}"]`
        )
      : null;

    /* Get consts */
    const select = customSelect.querySelector<HTMLElement>(
      "[js-custom-select-button]"
    );
    const dropdown = customSelect.querySelector<HTMLElement>(
      "[js-custom-select-dropdown]"
    );
    const dropdownItems = customSelect.querySelectorAll<HTMLElement>(
      "[js-custom-select-dropdown] li"
    );
    const activeDropdownClass = "custom-dropdown--active";

    if (!select || !dropdown || !dropdownItems) {
      return;
    }

    /* Hide dropdown */
    const closeDropdown = () => {
      dropdown.classList.remove(activeDropdownClass);
      dropdown.setAttribute("aria-expanded", "false");
      flipChevron(select, CHEVRON_DOWN);

      window.removeEventListener("keydown", handleKeyDown);
    };

    const moveUpDownItem = (
      down: boolean,
      items: NodeListOf<HTMLElement>,
      currentIndex: number
    ) => {
      let target = items[0];

      if (down) {
        if (currentIndex++ <= items.length) {
          target = items[currentIndex++];
        } else {
          return;
        }
      } else {
        if (currentIndex-- >= 0) {
          target = items[currentIndex--];
        } else {
          return;
        }
      }

      target.focus();
    };

    /* List item gets selected */
    const selectItem = (event: KeyboardEvent | MouseEvent) => {
      const itemElement = event.currentTarget as HTMLElement;
      select.textContent = itemElement.textContent;

      /* Need to get item value through getAttribute as it's not part of HTMLElement */
      const itemValue = itemElement.getAttribute("value");

      /* Perform explicit null check to allow empty strings to be selected */
      if (targetFormSelect && itemValue != null) {
        /* If the custom select is associated with a real one, update its value */
        targetFormSelect.setAttribute("value", itemValue);

        /* Update the selected option to match */
        const selectedOpt = targetFormSelect.querySelector("option[selected]");
        const nextOpt = targetFormSelect.querySelector(
          `option[value='${itemValue}']`
        );

        if (selectedOpt) {
          selectedOpt.removeAttribute("selected");
        }

        if (nextOpt) {
          nextOpt.setAttribute("selected", "selected");
        }

        /* Simulating a click gets the MVC validation framework to check the value again */
        targetFormSelect.click();
      }

      closeDropdown();
    };

    if (targetFormSelect) {
      /* If the custom select is associated with a select element, 
      ensure any changes to the select are reflected in the custom element */
      targetFormSelect.addEventListener("change", () => {
        const selectedItem = targetFormSelect.options[
          targetFormSelect.selectedIndex
        ] as HTMLOptionElement;

        if (selectedItem) {
          const text = selectedItem.innerText.trim();

          select.textContent = text;
        }
      });
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      const { key } = e;

      if (key.length > 1) {
        // only do the jump if the key entered is a single character
        // excludes presses of Enter, Space etc.
        return;
      }

      const firstMatchingOpt = [...dropdownItems].find((elt) => {
        const lowerKey = key.toLowerCase();
        const firstChar = elt.innerText.trim()[0].toLowerCase();
        return firstChar === lowerKey;
      });

      if (firstMatchingOpt) {
        dropdown.scrollTo(0, firstMatchingOpt.offsetTop);
      }
    };

    const openDropdown = () => {
      dropdown.classList.toggle(activeDropdownClass);
      dropdown.setAttribute(
        "aria-expanded",
        dropdown.classList.contains(activeDropdownClass).toString()
      );
      flipChevron(select);

      // set up keydown handler for jumping
      window.addEventListener("keydown", handleKeyDown);
    };

    /* Click select button, toggle visibility */
    select.addEventListener("click", function () {
      openDropdown();
    });

    dropdownItems.forEach((element, index) => {
      /* List item is clicked, set as selected */
      element.addEventListener("click", (event) => {
        selectItem(event);
      });

      /* Keyboard events */
      element.addEventListener("keydown", (event) => {
        switch (event.code) {
          /* Select item if ENTER is pressed */
          case ENTER_KEY_CODE:
            selectItem(event);
            return;

          /* Close if ESC is pressed */
          case ESCAPE_KEY_CODE:
            closeDropdown();
            return;

          /* Up if ESC is pressed */
          case UP_KEY_CODE:
            moveUpDownItem(false, dropdownItems, index);

            /* Down if ESC is pressed */
            return;
          case DOWN_KEY_CODE:
            moveUpDownItem(true, dropdownItems, index);

            return;

          default:
            return;
        }
      });
    });

    /* Close dropdown if anywhere outside the dropdown is clicked */
    window.addEventListener("click", (e) => {
      if (!dropdown.classList.contains(activeDropdownClass)) {
        return;
      }

      if (!select.contains(e.target as Node)) {
        closeDropdown();
      }
    });

    const flipChevron = (select: HTMLElement, direction?: string) => {
      if (direction === CHEVRON_UP) {
        select.parentElement?.classList.add("chevron-up");
      } else if (direction === CHEVRON_DOWN) {
        select.parentElement?.classList.remove("chevron-up");
      } else {
        select.parentElement?.classList.toggle("chevron-up");
      }
    };
  };

  /* Get all custom selects on page */
  const customSelects =
    document.querySelectorAll<HTMLElement>("[js-custom-select]");

  /* Loop over all of the custom selects and apply functionality on each one */
  customSelects.length > 0
    ? customSelects.forEach((customSelect) => customSelectInit(customSelect))
    : null;
};
